/**
 * Maps `theme/base.css`'s `.ctl` control language onto MUI's `OutlinedInput`
 * — needed at the theme level, not via a `className`, because the visible
 * border/background come from `OutlinedInput`'s internal `fieldset`/
 * `notchedOutline`, an element the legacy CSS has no selector for at all.
 * One override here reaches every `TextField`/`Select`/`Autocomplete` in the
 * app; a per-call-site className could not.
 */
import type { Components, Theme } from '@mui/material/styles';

export const MuiOutlinedInput: Components<Theme>['MuiOutlinedInput'] = {
	styleOverrides: {
		root: {
			backgroundColor: 'var(--field)',
			borderRadius: 'var(--r-sm)',
			'& .MuiOutlinedInput-notchedOutline': {
				borderColor: 'var(--line-strong)',
			},
			'&:hover:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': {
				borderColor: 'var(--ink-3)',
			},
			'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
				borderColor: 'var(--accent)',
				borderWidth: '1px',
				boxShadow: '0 0 0 3px var(--accent-surface)',
			},
			'&.Mui-error .MuiOutlinedInput-notchedOutline': {
				borderColor: 'var(--danger-solid)',
			},
			'&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': {
				boxShadow: '0 0 0 3px var(--danger-surface)',
			},
			/* The field's own focused border (above) is the focus indicator. The
         global `input:focus` halo and `input:focus-visible` outline in
         base.css are for plain inputs; on the <input> inside this field they
         would draw a second ring, so keep just the one. */
			'& .MuiOutlinedInput-input': {
				'&:focus, &:focus-visible': {
					outline: 'none',
					boxShadow: 'none',
				},
			},
			'&.Mui-disabled': {
				backgroundColor: 'var(--surface-sunken)',
			},
			'&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
				borderColor: 'var(--line)',
			},
		},
		input: {
			/* `!important` on the four properties the base.css `.ctl` rule
         also sets on every raw <input> in the page — that selector
         (`input:not([type=button],...)`) is a "deny list", written to catch
         every input including this one, and it's MORE specific than a plain
         `.MuiOutlinedInput-input` class, so it wins the properties they both
         set: a real double-border was the result, not a cosmetic nitpick.
         The fieldset drawn by `root` above is the only border that should be
         visible; the input itself must be borderless/transparent so it
         doesn't paint a second box on top. */
			border: 'none !important',
			background: 'transparent !important',
			borderRadius: '0 !important',
			minHeight: '0 !important',
			padding: '0 var(--s3)',
			height: 'var(--ctl-h)',
			boxSizing: 'border-box',
			fontFamily: 'var(--font-sans)',
			fontSize: 'var(--fs-body)',
			lineHeight: 'var(--lh-snug)',
			color: 'var(--ink)',
			'&::placeholder': {
				color: 'var(--ink-3)',
				opacity: 1,
			},
			/* MUI's `Autocomplete` (and some other controls) select their own
         input text on focus, so typing immediately replaces the value —
         real, deliberate MUI behaviour, not the reader manually
         highlighting something. The app's global `::selection` (an
         intentional design choice for actual reading/copying, see
         `base.css`) renders that the same loud brand green either way,
         which reads as a stuck highlight box rather than "this field is
         focused". Scoped here, to MUI's own input only — not a global
         rule. `!important`, confirmed necessary by DevTools: this rule's
         `.css-…::selection` selector already outranks the global bare
         `::selection` on pure specificity (one class beats none) and was
         still losing — `::selection`'s cascade doesn't play by the usual
         rules in every engine, so this is forced rather than trusted. */
			'&::selection': {
				background: 'var(--line-strong) !important',
				color: 'var(--ink) !important',
			},
		},
		/* `size="small"` is used throughout for density, but this app has one
       control height, not two — so `sizeSmall` gets the same `--ctl-h`
       rather than a shorter MUI default. */
		sizeSmall: {
			'& .MuiOutlinedInput-input': {
				padding: '0 var(--s3)',
				height: 'var(--ctl-h)',
			},
		},
		multiline: {
			height: 'auto',
			padding: 'var(--s2) var(--s3)',
		},
	},
};
